import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Modal, Button, Form, Tabs, Tab, Alert, Spinner,
} from 'react-bootstrap';
import { ConfirmModal } from '../ConfirmModal/ConfirmModal';
import { useQnAConfigContext } from './contexts/QnAConfigContext';
import { QnAConfigAdvancedTab } from './QnAConfigAdvancedTab';
import { QnAConfigBackendOverrideTab } from './QnAConfigBackendOverrideTab';
import styles from './QnAConfigModal.module.scss';
import { AIServiceConfig, AIServiceConfigValidation } from './services';

export function QnAConfigModal() {
  const {
    isOpen,
    activeTab,
    config,
    validation,
    isValidating,
    isSaving,
    hasUnsavedChanges,
    closeConfigModal,
    setActiveTab,
    updateConfig,
    validateField,
    saveConfiguration,
    resetToDefaults,
    exportConfiguration,
    importConfiguration,
  } = useQnAConfigContext();

  const [importFile, setImportFile] = useState<File | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setImportFile(null);
      setShowResetConfirm(false);
      setShowCloseConfirm(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirm(true);
      return;
    }
    closeConfigModal();
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    closeConfigModal();
  };

  const handleCancelClose = () => {
    setShowCloseConfirm(false);
  };

  const handleSave = async () => {
    const success = await saveConfiguration();
    if (success) {
      closeConfigModal();
    }
  };

  const handleReset = async () => {
    try {
      await resetToDefaults();
      setShowResetConfirm(false);
    } catch (error) {
      // Handle error silently
    }
  };

  const handleExport = async () => {
    try {
      await exportConfiguration();
    } catch (error) {
      // Handle error silently
    }
  };

  const handleImport = async () => {
    if (!importFile) return;

    try {
      const text = await importFile.text();
      const success = await importConfiguration(text);
      if (success) {
        setImportFile(null);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const handleFieldChange = async (field: keyof AIServiceConfig, value: unknown) => {
    await updateConfig({ [field]: value });
    await validateField(field, value);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setImportFile(e.target.files?.[0] || null);
  };

  const renderValidationFeedback = (field: keyof AIServiceConfigValidation) => {
    const fieldValidation = validation[field];
    if (!fieldValidation || fieldValidation.isValid) return null;

    return (
      <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
        {fieldValidation.error}
      </Form.Control.Feedback>
    );
  };

  return (
    <>
      <Modal
        show={isOpen}
        onHide={handleClose}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
        className={styles.configurationModal}
      >
        <Modal.Header>
          <Modal.Title>
            QnA Settings
            {isValidating && <Spinner animation="border" size="sm" className="ms-2" />}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={(e) => e.preventDefault()}>
            <Tabs
              activeKey={activeTab}
              onSelect={(tab) => setActiveTab(tab as 'backend-override' | 'advanced')}
              className="mb-3"
            >
              <Tab eventKey="backend-override" title="Backend Override">
                <QnAConfigBackendOverrideTab
                  config={config}
                  validation={validation}
                  onFieldChange={handleFieldChange}
                  renderValidationFeedback={renderValidationFeedback}
                />
              </Tab>
              <Tab eventKey="advanced" title="Advanced">
                <QnAConfigAdvancedTab
                  config={config}
                  validation={validation}
                  importFile={importFile}
                  showResetConfirm={showResetConfirm}
                  onFieldChange={handleFieldChange}
                  onFileChange={handleFileChange}
                  onExport={handleExport}
                  onImport={handleImport}
                  onResetClick={() => setShowResetConfirm(true)}
                  onResetConfirm={handleReset}
                  onResetCancel={() => setShowResetConfirm(false)}
                  renderValidationFeedback={renderValidationFeedback}
                />
              </Tab>
            </Tabs>

            {hasUnsavedChanges && (
              <Alert variant="warning">
                <small>
                  <strong>Unsaved changes!</strong>
                  {' '}
                  Don&apos;t forget to save your changes before closing.
                </small>
              </Alert>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={showCloseConfirm}
        isLoading={false}
        title="Unsaved Changes"
        confirmButtonTitle="Yes, Close"
        cancelButtonTitle="Cancel"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      >
        <p>You have unsaved changes. Are you sure you want to close the settings window?</p>
      </ConfirmModal>
    </>
  );
}
